<script lang="ts">
  import { link } from "svelte-spa-router";
  import DeviceConfig from "./DeviceConfig.svelte";
  import Features from "./Features.svelte";
  import LedColors from "./LedColors.svelte";
  import Messages from "./Messages.svelte";
  import FlashFirmware from "./FlashFirmware.svelte";
  import MediaDiagnostics from "../lib/MediaDiagnostics.svelte";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import {
    listDevices,
    listRooms,
    listSchools,
    listAuthorities,
    setDeviceRoom,
    setDeviceForbidInvisible,
    setDeviceForceE2eeQr,
    setDeviceForceE2ee,
    setDeviceE2eeRotateHour,
    setDeviceLogLevel,
    getDeviceSignPubkey,
    type ReleaseChannel,
  } from "../lib/api";
  import { scopes } from "../lib/session.svelte";
  import {
    LOG_LEVELS,
    type AdminRoom,
    type Authority,
    type School,
    type LogLevel,
    type DeviceListEntry as BaseDeviceListEntry,
  } from "@diy-avatar/shared/admin";

  interface DeviceReleaseInfo {
    installed_tag?: string | null;
    installed_commit?: string | null;
    release_time?: string | null;
    channel?: ReleaseChannel | null;
    update?: {
      available: boolean;
      target_tag: string;
      releases_behind: number;
      commits_behind: number;
    } | null;
  }
  type DeviceEntry = BaseDeviceListEntry & DeviceReleaseInfo;

  // svelte-spa-router passes route params via the `params` prop.
  export let params: { label?: string } = {};

  $: label = params.label ? decodeURIComponent(params.label) : undefined;

  let entry: DeviceEntry | null = null;
  let rooms: AdminRoom[] = [];
  let schools: School[] = [];
  let authorities: Authority[] = [];
  let roomError: string | null = null;
  let forbidError: string | null = null;
  let forceQrError: string | null = null;
  let forceE2eeError: string | null = null;
  let rotateHourError: string | null = null;
  let signPubkey: string | null = null;
  let signPubkeyError = "";
  let signPubkeyLoading = false;
  let logLevelError: string | null = null;
  let logLevelSaved = false;

  async function loadEntry(l: string) {
    try {
      const res = await listDevices();
      entry = (res.devices as DeviceEntry[]).find((d) => d.label === l) ?? null;
    } catch {
      entry = null;
    }
  }

  async function loadSignPubkey(l: string) {
    signPubkeyLoading = true;
    signPubkeyError = "";
    try {
      const res = await getDeviceSignPubkey(l);
      signPubkey = res.sign_pubkey;
    } catch (err) {
      signPubkey = null;
      signPubkeyError = err instanceof Error ? err.message : "load failed";
    } finally {
      signPubkeyLoading = false;
    }
  }

  async function loadRooms() {
    try {
      rooms = (await listRooms()).rooms;
    } catch {
      rooms = [];
    }
  }

  async function loadHierarchy() {
    try {
      schools = (await listSchools()).schools;
    } catch {
      schools = [];
    }
    try {
      authorities = (await listAuthorities()).authorities;
    } catch {
      authorities = [];
    }
  }

  async function toggleForbidInvisible(checked: boolean) {
    if (!label) return;
    forbidError = null;
    try {
      await setDeviceForbidInvisible(label, checked);
      await loadEntry(label);
    } catch (err) {
      forbidError = err instanceof Error ? err.message : "save failed";
    }
  }

  async function toggleForceE2eeQr(checked: boolean) {
    if (!label) return;
    forceQrError = null;
    try {
      await setDeviceForceE2eeQr(label, checked);
      await loadEntry(label);
    } catch (err) {
      forceQrError = err instanceof Error ? err.message : "save failed";
    }
  }

  async function toggleForceE2ee(checked: boolean) {
    if (!label) return;
    forceE2eeError = null;
    try {
      await setDeviceForceE2ee(label, checked);
      await loadEntry(label);
    } catch (err) {
      forceE2eeError = err instanceof Error ? err.message : "save failed";
    }
  }

  async function changeRotateHour(raw: string) {
    if (!label) return;
    rotateHourError = null;
    const hour = raw === "" ? null : parseInt(raw, 10);
    if (hour !== null && (!Number.isInteger(hour) || hour < 0 || hour > 23)) {
      rotateHourError = "rotation hour must be 0-23";
      return;
    }
    try {
      await setDeviceE2eeRotateHour(label, hour);
      await loadEntry(label);
    } catch (err) {
      rotateHourError = err instanceof Error ? err.message : "save failed";
    }
  }

  async function changeLogLevel(value: string) {
    if (!label) return;
    const level = value as LogLevel;
    logLevelError = null;
    logLevelSaved = false;
    try {
      await setDeviceLogLevel(label, level);
      logLevelSaved = true;
      setTimeout(() => (logLevelSaved = false), 1500);
      await loadEntry(label);
    } catch (err) {
      logLevelError = err instanceof Error ? err.message : "save failed";
    }
  }

  async function changeRoom(roomId: number | null) {
    if (!label) return;
    roomError = null;
    try {
      await setDeviceRoom(label, roomId);
      await loadEntry(label);
    } catch (err) {
      roomError = err instanceof Error ? err.message : "set room failed";
    }
  }

  $: room = entry?.room_id != null ? rooms.find((r) => r.id === entry!.room_id) ?? null : null;
  $: school = room ? schools.find((s) => s.id === room.school_id) ?? null : null;
  $: authority = school ? authorities.find((a) => a.id === school.authority_id) ?? null : null;
  // Inherited indicator: forbidden by the room, its school, or its authority.
  $: parentForbidSource = room?.forbid_invisible
    ? `room${room.name ? ` (${room.name})` : ""}`
    : school?.forbid_invisible
      ? `school${school.name ? ` (${school.name})` : ""}`
      : authority?.forbid_invisible
        ? `authority${authority.name ? ` (${authority.name})` : ""}`
        : null;
  // Inherited indicator: QR forced by the room, its school, or its authority.
  $: parentForceQrSource = room?.force_e2ee_qr
    ? `room${room.name ? ` (${room.name})` : ""}`
    : school?.force_e2ee_qr
      ? `school${school.name ? ` (${school.name})` : ""}`
      : authority?.force_e2ee_qr
        ? `authority${authority.name ? ` (${authority.name})` : ""}`
        : null;
  // Inherited indicator: E2EE forced by the room, its school, or its authority.
  $: parentForceE2eeSource = room?.force_e2ee
    ? `room${room.name ? ` (${room.name})` : ""}`
    : school?.force_e2ee
      ? `school${school.name ? ` (${school.name})` : ""}`
      : authority?.force_e2ee
        ? `authority${authority.name ? ` (${authority.name})` : ""}`
        : null;
  // Inherited rotation hour: most-specific parent that has it set (room →
  // school → authority), shown only when the device's own value is null.
  $: parentRotateHourSource =
    room?.e2ee_rotate_hour != null
      ? `room${room.name ? ` (${room.name})` : ""}: ${String(room.e2ee_rotate_hour).padStart(2, "0")}:00`
      : school?.e2ee_rotate_hour != null
        ? `school${school.name ? ` (${school.name})` : ""}: ${String(school.e2ee_rotate_hour).padStart(2, "0")}:00`
        : authority?.e2ee_rotate_hour != null
          ? `authority${authority.name ? ` (${authority.name})` : ""}: ${String(authority.e2ee_rotate_hour).padStart(2, "0")}:00`
          : "default 00:00";
  $: canManage = $scopes.isAdmin;
  $: e2eeState = entry
    ? entry.e2ee_key_id
      ? "encrypted"
      : entry.force_encryption
        ? "force"
        : "off"
    : "off";

  $: if (label) {
    void loadEntry(label);
    void loadRooms();
    void loadHierarchy();
    void loadSignPubkey(label);
  }

  function shortHash(h: string | null | undefined): string {
    return h ? h.slice(0, 7) : "";
  }
  function dateOnly(s: string | null | undefined): string {
    return s ? s.slice(0, 10) : "";
  }
  function dateTime(s: string | null | undefined): string {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleString();
  }

  // A heartbeat older than this is no longer evidence of anything: the device
  // could have been unplugged right after it. ~3x the agent heartbeat cadence.
  const LED_STALE_AFTER_S = 45;
  /**
   * LED hardware link, aged out. Returns "unknown" when the device is offline,
   * the heartbeat is stale, or the agent does not report the field — showing
   * the last known good value there is exactly the bug this guards against.
   */
  function ledLinkText(d: DeviceEntry | null): "connected" | "disconnected" | "unknown" {
    if (!d || !d.online) return "unknown";
    const age = d.last_heartbeat_age_s;
    if (age == null || age > LED_STALE_AFTER_S) return "unknown";
    if (typeof d.led_connected !== "boolean") return "unknown";
    return d.led_connected ? "connected" : "disconnected";
  }
</script>

<section>
  <Breadcrumb
    items={room
      ? [
          { label: "Schools", href: "/schools" },
          { label: room.school_name ?? "School", href: `/schools/${room.school_id}` },
          { label: room.name, href: `/rooms/${room.id}` },
          { label: label ?? "—" },
        ]
      : [
          { label: "Devices", href: "/devices" },
          { label: label ?? "—" },
        ]}
  />
  <div class="head">
    <a href="/devices" use:link data-testid="back-devices">← Devices</a>
    <h1 data-testid="device-detail-label">{label ?? "—"}</h1>
  </div>

  {#if label}
    {#key label}
      <div class="panel release" data-testid="detail-context">
        <h2>Location</h2>
        <dl>
          <dt>Room</dt>
          <dd data-testid="detail-room">
            {#if room}
              <a href="/rooms/{room.id}" use:link>{room.name}</a>
              <span class="muted"> · </span>
              <a href="/schools/{room.school_id}" use:link>{room.school_name ?? "school"}</a>
            {:else}
              <span class="muted">unassigned</span>
            {/if}
          </dd>
          <dt>Assign room</dt>
          <dd>
            <select
              value={entry?.room_id ?? ""}
              on:change={(e) =>
                changeRoom(e.currentTarget.value === "" ? null : Number(e.currentTarget.value))}
              data-testid="detail-room-select"
            >
              <option value="">— unassigned —</option>
              {#each rooms as r (r.id)}
                <option value={r.id}>{r.name}</option>
              {/each}
            </select>
            {#if roomError}<span class="error"> {roomError}</span>{/if}
          </dd>
        </dl>
      </div>
      <div class="panel release" data-testid="detail-settings">
        <h2>Settings</h2>
        <label class="toggle">
          <input
            type="checkbox"
            checked={entry?.forbid_invisible ?? false}
            disabled={!canManage}
            on:change={(e) => toggleForbidInvisible(e.currentTarget.checked)}
            data-testid="forbid-invisible"
          />
          Forbid invisible mode
        </label>
        {#if parentForbidSource}
          <span class="inherited" data-testid="forbid-invisible-inherited">
            Already forbidden by {parentForbidSource}
          </span>
        {/if}
        {#if forbidError}<span class="error"> {forbidError}</span>{/if}
        <label class="toggle">
          <input
            type="checkbox"
            checked={entry?.force_e2ee_qr ?? false}
            disabled={!canManage}
            on:change={(e) => toggleForceE2eeQr(e.currentTarget.checked)}
            data-testid="force-e2ee-qr"
          />
          QR-Schlüsselaustausch erzwingen
        </label>
        {#if parentForceQrSource}
          <span class="inherited" data-testid="force-e2ee-qr-inherited">
            Bereits erzwungen durch {parentForceQrSource}
          </span>
        {/if}
        {#if forceQrError}<span class="error"> {forceQrError}</span>{/if}
        <p class="hint e2ee-qr-desc">
          <strong>QR-Schlüsselaustausch erzwingen.</strong> Wenn aktiv, müssen
          Schüler:innen den QR-Code am Klassen-Display <strong>persönlich
          scannen</strong>. Der Schlüssel verlässt dabei nie unsere Server —
          höchste Sicherheit, aber alle müssen physisch anwesend sein. Wenn
          deaktiviert, werden Schlüssel automatisch verteilt (für jedes
          Schüler-Gerät versiegelt), sodass auch entfernte Schüler:innen ohne
          Scannen verschlüsselt teilnehmen — das vertraut darauf, dass der
          Server korrekte öffentliche Schlüssel ausliefert. Aktivierung auf
          <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
          erzwingt QR für <strong>alle</strong> darunterliegenden Geräte und
          Schüler:innen.
        </p>
        <label class="toggle">
          <input
            type="checkbox"
            checked={entry?.force_e2ee ?? false}
            disabled={!canManage}
            on:change={(e) => toggleForceE2ee(e.currentTarget.checked)}
            data-testid="force-e2ee"
          />
          Verschlüsselung erzwingen
        </label>
        {#if parentForceE2eeSource}
          <span class="inherited" data-testid="force-e2ee-inherited">
            Bereits erzwungen durch {parentForceE2eeSource}
          </span>
        {/if}
        {#if forceE2eeError}<span class="error"> {forceE2eeError}</span>{/if}
        <p class="hint e2ee-desc">
          <strong>Verschlüsselung erzwingen.</strong> Wenn aktiv, ist
          Ende-zu-Ende-Verschlüsselung für alle Geräte und Schüler:innen darunter
          verpflichtend (kein Opt-out, nicht unterstützte Browser werden
          blockiert). Schlüssel werden weiterhin
          <strong>automatisch verteilt</strong> (für jedes Schüler-Gerät
          versiegelt) — anders als „QR erzwingen“, das die automatische
          Verteilung abschaltet. Aktivierung auf
          <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
          erzwingt Verschlüsselung für <strong>alle</strong> darunterliegenden
          Geräte und Schüler:innen.
        </p>
        <label class="field" for="device-rotate-hour">
          Tägliche E2EE-Schlüsselrotation (Stunde, Ortszeit)
          <select
            id="device-rotate-hour"
            value={entry?.e2ee_rotate_hour == null
              ? ""
              : String(entry.e2ee_rotate_hour)}
            disabled={!canManage}
            on:change={(e) => changeRotateHour(e.currentTarget.value)}
            data-testid="e2ee-rotate-hour"
          >
            <option value="">Vererbt (Standard)</option>
            {#each Array(24) as _, h}
              <option value={String(h)}>{String(h).padStart(2, "0")}:00</option>
            {/each}
          </select>
          {#if rotateHourError}<span class="error" data-testid="e2ee-rotate-hour-error"> {rotateHourError}</span>{/if}
        </label>
        {#if entry?.e2ee_rotate_hour == null}
          <span class="inherited" data-testid="e2ee-rotate-hour-inherited">
            Geerbt — {parentRotateHourSource}
          </span>
        {/if}
        <p class="hint">
          Stunde (0-23, Ortszeit), zu der der E2EE-Medienschlüssel täglich
          rotiert wird. Leer = von der übergeordneten Ebene erben (Standard
          00:00).
        </p>
        <label class="field loglevel" for="device-loglevel">
          Log level
          <select
            id="device-loglevel"
            value={entry?.loglevel ?? "info"}
            disabled={!canManage}
            on:change={(e) => changeLogLevel(e.currentTarget.value)}
            data-testid="device-loglevel"
          >
            {#each LOG_LEVELS as lvl}
              <option value={lvl}>{lvl}</option>
            {/each}
          </select>
          {#if logLevelSaved}<span class="ok" data-testid="device-loglevel-saved"> saved</span>{/if}
          {#if logLevelError}<span class="error" data-testid="device-loglevel-error"> {logLevelError}</span>{/if}
        </label>
        <p class="hint loglevel-desc">
          Effective log verbosity reported by this device. Raising it produces
          more detailed diagnostics; lower it again when finished. For a
          time- or budget-scoped boost, use the
          <a href="/debug" use:link>Debug</a> page.
        </p>
      </div>
      <div class="panel release" data-testid="detail-hardware">
        <h2>Hardware</h2>
        <dl>
          <dt>LED / on-air link</dt>
          <dd data-testid="detail-led-link">
            {#if ledLinkText(entry) === "connected"}
              <span class="ok">connected</span>
            {:else if ledLinkText(entry) === "disconnected"}
              <span class="error">disconnected</span>
            {:else}
              <span class="muted">unknown (no fresh heartbeat)</span>
            {/if}
          </dd>
          <dt>Last seen live</dt>
          <dd data-testid="detail-led-seen">
            {entry?.led_seen_ms ? new Date(entry.led_seen_ms).toLocaleString() : "—"}
          </dd>
        </dl>
      </div>
      <div class="panel release" data-testid="detail-release">
        <h2>Release</h2>
        <dl>
          <dt>Installed</dt>
          <dd data-testid="detail-installed-tag">{entry?.installed_tag ?? "—"}</dd>
          <dt>Commit</dt>
          <dd>{shortHash(entry?.installed_commit) || "—"}</dd>
          <dt>Release time</dt>
          <dd>{dateTime(entry?.release_time) || "—"}</dd>
          <dt>Channel</dt>
          <dd data-testid="detail-channel">{entry?.channel ?? "—"}</dd>
          <dt>Update</dt>
          <dd data-testid="detail-update">
            {#if entry?.update?.available}
              <span class="badge update">update</span>
              {entry.installed_tag ?? "?"} → {entry.update.target_tag}
              ({entry.update.releases_behind} releases,
              {entry.update.commits_behind} commits behind)
            {:else}
              <span class="ok">up to date</span>
            {/if}
          </dd>
        </dl>
      </div>
      <div class="panel release" data-testid="detail-e2ee">
        <h2>Encryption</h2>
        <dl>
          <dt>Status</dt>
          <dd data-testid="detail-e2ee-status">
            {#if e2eeState === "encrypted"}
              <span class="enc-badge enc-encrypted">🔒 Encrypted</span>
            {:else if e2eeState === "force"}
              <span class="enc-badge enc-force">🔓 Force-encryption pending</span>
            {:else}
              <span class="enc-badge enc-off">🔓 Off</span>
            {/if}
          </dd>
          <dt>Force encryption</dt>
          <dd>{entry?.force_encryption ? "Yes" : "No"}</dd>
          <dt>Active key id</dt>
          <dd data-testid="detail-e2ee-key">{entry?.e2ee_key_id ?? "—"}</dd>
          <dt>QR key exchange forced</dt>
          <dd>{entry?.force_e2ee_qr ? "Yes" : "No"}</dd>
          <dt>Encryption required</dt>
          <dd>{entry?.force_e2ee ? "Yes" : "No"}</dd>
          <dt>Key rotation hour</dt>
          <dd data-testid="detail-e2ee-rotate-hour">
            {entry?.e2ee_rotate_hour == null
              ? "inherit"
              : `${String(entry.e2ee_rotate_hour).padStart(2, "0")}:00`}
          </dd>
          <dt>Sign public key</dt>
          <dd data-testid="detail-e2ee-pubkey">
            {#if signPubkeyLoading}
              …
            {:else if signPubkeyError}
              <span class="error">{signPubkeyError}</span>
            {:else if signPubkey}
              <span title={signPubkey}>{signPubkey.slice(0, 16)}…</span>
            {:else}
              —
            {/if}
          </dd>
        </dl>
      </div>
      <div class="panel" data-testid="detail-config">
        <DeviceConfig {label} />
      </div>
      <div class="panel" data-testid="detail-media-diagnostics">
        <MediaDiagnostics {label} />
      </div>
      <div class="panel" data-testid="detail-features">
        <Features {label} />
      </div>
      <div class="panel" data-testid="detail-led-colors">
        <LedColors {label} />
      </div>
      <div class="panel" data-testid="detail-messages">
        <Messages {label} />
      </div>
      <div class="panel" data-testid="detail-flash">
        <FlashFirmware {label} />
      </div>
    {/key}
  {:else}
    <p class="error" data-testid="error">Missing device label.</p>
  {/if}
</section>

<style>
  .head {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
  .head h1 {
    margin: 0;
  }
  .head a {
    color: #34d399;
    text-decoration: none;
  }
  .head a:hover {
    text-decoration: underline;
  }
  .panel {
    margin-bottom: 1.5rem;
  }
  .release {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }
  .release h2 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    color: #34d399;
  }
  .release dl {
    margin: 0;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.2rem 0.75rem;
  }
  .release dt {
    color: #9ca3af;
  }
  .release dd {
    margin: 0;
  }
  .badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 700;
    margin-right: 0.25rem;
  }
  .badge.update {
    background: #3b2f06;
    color: #fbbf24;
  }
  .ok {
    color: #6ee7b7;
  }
  .muted {
    color: #6b7280;
  }
  .release dd a {
    color: #34d399;
    text-decoration: none;
  }
  .release dd a:hover {
    text-decoration: underline;
  }
  .release select {
    padding: 0.3rem 0.4rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  .error {
    color: #fca5a5;
  }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  .toggle input {
    padding: 0;
  }
  .inherited {
    margin-left: 0.6rem;
    color: #fbbf24;
    font-size: 0.78rem;
  }
  .e2ee-qr-desc {
    color: #d1d5db;
    background: #1c1407;
    border-left: 3px solid #b45309;
    padding: 0.6rem 0.8rem;
    border-radius: 4px;
    line-height: 1.4;
    font-size: 0.8rem;
    margin: 0.5rem 0 0;
  }
  .e2ee-qr-desc strong { color: #fbbf24; }
  .field {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #9ca3af;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }
  .field select {
    padding: 0.3rem 0.4rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  .field.loglevel {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #9ca3af;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }
  .field.loglevel select {
    padding: 0.3rem 0.4rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  .loglevel-desc {
    color: #d1d5db;
    font-size: 0.8rem;
    line-height: 1.4;
    margin: 0.4rem 0 0;
  }
  .loglevel-desc a { color: #34d399; text-decoration: none; }
  .loglevel-desc a:hover { text-decoration: underline; }
  .enc-badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.78rem;
    font-weight: 700;
  }
  .enc-badge.enc-encrypted {
    background: #052e1a;
    color: #34d399;
  }
  .enc-badge.enc-force {
    background: #3b2f06;
    color: #fbbf24;
  }
  .enc-badge.enc-off {
    background: #1f2937;
    color: #9ca3af;
  }
</style>
